import { useCallback, useEffect, useRef, useState } from 'react';
import type { BoardRow } from '@shared/types';

/**
 * Row order that does not move under the pointer.
 *
 * This is the single most important behaviour on the board. Values inside a row
 * tick live; row POSITIONS do not drift. Three rules make that true:
 *
 *  1. Scores are quantised into ten-point bands before sorting, so a fractional
 *     wobble can never reorder anything.
 *  2. Re-ranking happens on a 15-second tick, not on every patch.
 *  3. A tick that lands while the pointer is over the table — or while focus is
 *     inside it — is DEFERRED, not applied. The operator is told the order is
 *     stale and can apply it themselves; otherwise it applies the moment they
 *     leave. A row that jumps out from under a click is a bug, and no amount of
 *     animation makes it acceptable.
 *
 * New rows are the exception: they appear immediately, at the top, where the
 * arrival highlight will catch the eye. Nothing that was already on screen
 * moves to make room except by shifting down one place.
 */

const STATE_RANK: Record<BoardRow['handlingState'], number> = {
  parked: 0,
  human: 1,
  ai: 2,
  resolved: 3,
};

/** Ten-point buckets. A 0.4-point change must not reorder the board. */
const band = (row: BoardRow): number => Math.round(row.attentionScore / 10);

const activityAt = (row: BoardRow): number =>
  row.lastMessageAt === null ? 0 : new Date(row.lastMessageAt).getTime();

export function rank(rows: BoardRow[]): string[] {
  return [...rows]
    .sort(
      (a, b) =>
        band(b) - band(a) ||
        STATE_RANK[a.handlingState] - STATE_RANK[b.handlingState] ||
        activityAt(b) - activityAt(a) ||
        a.conversationId.localeCompare(b.conversationId),
    )
    .map((row) => row.conversationId);
}

/**
 * lib.dom already declares startViewTransition, and re-declaring it with a
 * narrower signature makes the interface incompatible with Document. Narrow
 * structurally instead of extending, so this keeps working whether or not the
 * ambient lib has it.
 */
type MaybeViewTransition = {
  startViewTransition?: (callback: () => void) => unknown;
};

export interface StableOrder {
  order: string[];
  /** True when a re-rank was held back because the operator was working. */
  staleOrder: boolean;
  sortNow: () => void;
  /** Spread onto the scroll container that holds the rows. */
  containerProps: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
    onFocusCapture: () => void;
    onBlurCapture: (event: React.FocusEvent<HTMLElement>) => void;
  };
}

export function useStableOrder(rows: BoardRow[], intervalMs = 15_000): StableOrder {
  const [order, setOrder] = useState<string[]>(() => rank(rows));
  const [staleOrder, setStaleOrder] = useState(false);
  const frozen = useRef(false);
  const latest = useRef(rows);
  latest.current = rows;

  const apply = useCallback((next: string[]) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const doc = document as MaybeViewTransition;
    // A View Transition FLIPs the rows into their new places in one 250ms
    // transform. It is the only place in the console anything moves, it only
    // ever runs when nobody is pointing at the table, and reduced-motion skips
    // it entirely for an instant swap.
    if (typeof doc.startViewTransition === 'function' && !reduce) {
      doc.startViewTransition(() => {
        setOrder(next);
      });
    } else {
      setOrder(next);
    }
    setStaleOrder(false);
  }, []);

  // Additions and removals are structural, not a re-rank: they apply at once.
  useEffect(() => {
    setOrder((previous) => {
      const live = new Set(rows.map((row) => row.conversationId));
      const kept = previous.filter((id) => live.has(id));
      const known = new Set(previous);
      const added = rank(rows.filter((row) => !known.has(row.conversationId)));
      if (added.length === 0 && kept.length === previous.length) return previous;
      return [...added, ...kept];
    });
  }, [rows]);

  useEffect(() => {
    const id = setInterval(() => {
      const next = rank(latest.current);
      if (frozen.current) {
        setOrder((previous) => {
          // Only claim the order is stale if it would actually have changed.
          const changed = next.length !== previous.length || next.some((v, i) => v !== previous[i]);
          if (changed) setStaleOrder(true);
          return previous;
        });
        return;
      }
      apply(next);
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [apply, intervalMs]);

  const sortNow = useCallback(() => {
    apply(rank(latest.current));
  }, [apply]);

  const thaw = useCallback(() => {
    frozen.current = false;
    setStaleOrder((stale) => {
      if (stale) apply(rank(latest.current));
      return stale ? false : stale;
    });
  }, [apply]);

  return {
    order,
    staleOrder,
    sortNow,
    containerProps: {
      onPointerEnter: () => {
        frozen.current = true;
      },
      onPointerLeave: thaw,
      onFocusCapture: () => {
        frozen.current = true;
      },
      onBlurCapture: (event) => {
        // Keyboard users get the same protection: only thaw when focus has
        // genuinely left the table, not when it moves between two rows.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        thaw();
      },
    },
  };
}
